import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { Form } from '../../_forms';
import { AlertService, ResolveSettingsService } from '../../_services';
import {
  FunnelInputTypeEnum,
  FunnelInputTypeLabels,
  PortalSessionTypes,
  ResolveActionConfigs,
  ResolveActionType,
  ResolveBuyAgainDestinations,
  ResolveSessionTypeLabels,
  ResolveSettings,
  ResolveSettingsMissingMessage,
  ResolveViewOrderStepTypesBySession,
  SessionType,
} from '../../_models';

@Component({
  selector: 'app-resolve-action-config',
  templateUrl: './resolve-action-config.component.html',
  styleUrls: ['./resolve-action-config.component.scss'],
})
export class ResolveActionConfigComponent extends Form implements OnInit {
  actionTypes = ResolveActionType;
  actionType: ResolveActionType;
  title = '';
  description = '';
  settings: ResolveSettings;
  isSaving = false;
  sessionTypeOptions = PortalSessionTypes;
  sessionTypeLabels = ResolveSessionTypeLabels;
  stepTypeLabels: { [type: number]: string } = FunnelInputTypeLabels;
  buyAgainDestinations = ResolveBuyAgainDestinations;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    private formBuilder: FormBuilder,
    private resolveSettingsService: ResolveSettingsService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.actionType = this.route.snapshot.data.actionType;
    this.title = ResolveActionConfigs[this.actionType].title;
    this.description = ResolveActionConfigs[this.actionType].description;

    this.resolveSettingsService
      .getAccountDefaultSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (settings) {
          this.settings = settings;
          this.buildForm(settings);
        } else {
          this.alertService.error(ResolveSettingsMissingMessage);
        }
      });
  }

  isViewOrder(): boolean {
    return this.actionType === this.actionTypes.ViewOrder;
  }

  get viewOrderStepTypes(): FunnelInputTypeEnum[] {
    const sessionType: SessionType = this.getControlValue('session_type');
    return ResolveViewOrderStepTypesBySession[sessionType] || [];
  }

  onSessionTypeChange() {
    const stepTypes = this.viewOrderStepTypes;
    const currentStep = this.getControlValue('view_order_step_type');

    if (stepTypes.indexOf(currentStep) === -1) {
      this.getControl('view_order_step_type').patchValue(stepTypes.length ? stepTypes[0] : null);
    }
  }

  apply() {
    if (this.settings) {
      this.isSaving = true;
      const payload = this.isViewOrder()
        ? {
            session_type: this.getControlValue('session_type'),
            view_order_step_type: this.getControlValue('view_order_step_type'),
          }
        : {
            buy_again_destination: this.getControlValue('destination'),
          };

      this.resolveSettingsService
        .patch(this.settings.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: ResolveSettings) => {
            this.settings = response;
            this.buildForm(response);
            this.alertService.success(this.title + ' settings saved successfully', true);
            this.isSaving = false;
            this.router.navigate(['/resolve-settings']);
          },
          (error) => {
            this.handleSubmitError(error, false, true);
            this.isSaving = false;
          }
        );
    }
  }

  private buildForm(settings: ResolveSettings) {
    this.setForm(
      this.formBuilder.group({
        session_type: [settings.session_type],
        view_order_step_type: [settings.view_order_step_type],
        destination: [settings.buy_again_destination],
      })
    );
  }
}
